import {useState} from 'react'
import {Box, Grid, Hero, PricingOptions, Tabs} from '@primer/react-brand'
// eslint-disable-next-line import/no-namespace
import * as Octicons from '@primer/octicons-react'
import {clsx} from 'clsx'

import {
  PLAN_TYPES,
  type PlanType,
} from '../../../../../../brand/components/PlanTypeSegmentedControl/PlanTypeSegmentedControl'

import Styles from './Pricing.module.css'

export interface PricingPlanFeatureItem {
  type: 'item'
  label: React.ReactNode
  info?: string
  iconName?: Exclude<keyof typeof Octicons, 'default'>
  iconColor?: string
}

export interface PricingPlanFeatureItemGroupHeading {
  type: 'groupHeading'
  label: React.ReactNode
}

export interface PricingPlan {
  type: PlanType
  name: string
  description: string
  price: {
    value: number
    originalPrice?: number
    trailingText?: string
    currencyCode?: string
    currencySymbol?: string
  }
  features: {
    heading: string
    items: Array<PricingPlanFeatureItem | PricingPlanFeatureItemGroupHeading>
  }
  cta1?: {
    label: string
    href: string
  }
  cta2?: {
    label: string
    href: string
  }
  footnote?: React.ReactNode
  highlightLabel?: string
  actionsMessage?: {
    content: React.ReactNode
  }
}

export interface PricingProps {
  label: string
  heading: string
  planTypes: Array<{
    label: string
    value: PlanType
  }>
  plans: PricingPlan[]
  a11y: {
    tabSelector: string
  }
}

export const Pricing = ({label, heading, planTypes, plans, a11y}: PricingProps) => {
  const [selectedPlanType, setSelectedPlanType] = useState<PlanType>(PLAN_TYPES.Individual)
  const filteredPlans = plans.filter(plan => plan.type === selectedPlanType)

  return (
    <div className={Styles['Pricing']}>
      <Hero variant="gridline" align="center" enableAnimation aria-label={heading} aria-labelledby={undefined}>
        <Hero.Label>{label}</Hero.Label>
        <Hero.Heading {...({as: 'h2'} as Record<string, unknown>)}>{heading}</Hero.Heading>
      </Hero>

      <div>
        <Box className={clsx(Styles['Pricing__gridContent'], Styles['Pricing__gridContentWide'])}>
          <Grid columnGap="none" rowGap="none" enableGutters={false}>
            <Grid.Column span={12} className={Styles['Pricing__gridColumn']}>
              <Tabs
                className={Styles['Pricing__tabsWrapper']}
                onChange={tabIndex => setSelectedPlanType(planTypes[+tabIndex]!.value)}
                aria-label={a11y.tabSelector}
                variant="accent"
              >
                {planTypes.map(planType => (
                  <Tabs.Item
                    key={planType.value}
                    id={`pricing-tab-${planType.value}`}
                    aria-controls="pricing-panel"
                    value={planType.value}
                  >
                    {planType.label}
                  </Tabs.Item>
                ))}
              </Tabs>
            </Grid.Column>
          </Grid>
        </Box>
      </div>

      <div id="pricing-panel" role="tabpanel" aria-labelledby={`pricing-tab-${selectedPlanType}`}>
        <Box className={Styles['Pricing__gridFrame']}>
          <Box className={clsx(Styles['Pricing__gridContent'], Styles['Pricing__gridContentWide'])}>
            <Grid columnGap="none" rowGap="none" enableGutters={false}>
              <Grid.Column span={12} className={Styles['Pricing__gridColumn']}>
                <PricingOptions className={Styles['Pricing__pricingTable']}>
                  {filteredPlans.map((plan, index) => (
                    <PricingOptions.Item
                      // eslint-disable-next-line @eslint-react/no-array-index-key
                      key={`plan-${selectedPlanType}-${index}`}
                      className={Styles['Pricing__pricingTable-item']}
                    >
                      {plan.highlightLabel && <PricingOptions.Label>{plan.highlightLabel}</PricingOptions.Label>}

                      <PricingOptions.Heading>
                        {plan.highlightLabel && <div className={Styles['Pricing__pricingTable-item-background']} />}
                        {plan.name}
                      </PricingOptions.Heading>

                      <PricingOptions.Description>{plan.description}</PricingOptions.Description>

                      <PricingOptions.Price
                        originalPrice={
                          plan.price.originalPrice !== undefined ? String(plan.price.originalPrice) : undefined
                        }
                        trailingText={plan.price.trailingText}
                        currencyCode={plan.price.currencyCode}
                        currencySymbol={plan.price.currencySymbol}
                      >
                        {plan.price.value}
                      </PricingOptions.Price>

                      {plan.actionsMessage ? (
                        <PricingOptions.ActionsMessage leadingComponent={<Octicons.InfoIcon />}>
                          {plan.actionsMessage.content}
                        </PricingOptions.ActionsMessage>
                      ) : null}

                      {plan.cta1 && (
                        <PricingOptions.PrimaryAction as="a" href={plan.cta1.href} variant="accent">
                          {plan.cta1.label}
                        </PricingOptions.PrimaryAction>
                      )}
                      {plan.cta2 && (
                        <PricingOptions.SecondaryAction as="a" href={plan.cta2.href}>
                          {plan.cta2.label}
                        </PricingOptions.SecondaryAction>
                      )}

                      <PricingOptions.FeatureListHeading key="feature-list-heading">
                        {plan.features.heading}
                      </PricingOptions.FeatureListHeading>
                      <PricingOptions.FeatureList>
                        {plan.features.items.map((item, itemIndex) =>
                          item.type === 'groupHeading' ? (
                            <PricingOptions.FeatureListGroupHeading
                              // eslint-disable-next-line @eslint-react/no-array-index-key
                              key={`item-${itemIndex}`}
                            >
                              {item.label}
                            </PricingOptions.FeatureListGroupHeading>
                          ) : (
                            <PricingOptions.FeatureListItem
                              // eslint-disable-next-line @eslint-react/no-array-index-key
                              key={`item-${itemIndex}`}
                              infoTooltip={item.info}
                              leadingVisual={item.iconName ? Octicons[item.iconName] : undefined}
                              leadingVisualFill={item.iconColor || undefined}
                            >
                              {item.label}
                            </PricingOptions.FeatureListItem>
                          ),
                        )}
                      </PricingOptions.FeatureList>

                      {plan.footnote ? <PricingOptions.Footnote>{plan.footnote}</PricingOptions.Footnote> : null}
                    </PricingOptions.Item>
                  ))}
                </PricingOptions>
              </Grid.Column>
            </Grid>
          </Box>
        </Box>
      </div>
    </div>
  )
}
